import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import {
  selectIsAuthenticated,
  selectCustomerId,
  selectCurrentUser,
} from '../../features/auth/authSlice';
import {
  useGetPropertyNeedQuery,
  useRespondToPropertyNeedMutation,
  useUpdatePropertyNeedResponseStatusMutation,
  useClosePropertyNeedMutation,
} from '../../features/api/apiSlice';
import { isProfessionalRole } from '../../lib/authPortals';
import UserLayout from '../user-dashboard/UserLayout';
import AgentLayout from '../agent-dashboard/AgentLayout';
import { trackEvent } from '../../lib/analytics';

const txStyles = {
  rent: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  buy: 'bg-violet-50 text-violet-700 border-violet-100',
  lease: 'bg-cyan-50 text-cyan-800 border-cyan-100',
  invest: 'bg-orange-50 text-orange-800 border-orange-100',
};

const statusStyles = {
  open: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
  fulfilled: 'bg-sky-50 text-sky-700 border-sky-100',
  pending: 'bg-amber-50 text-amber-800 border-amber-100',
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  declined: 'bg-rose-50 text-rose-700 border-rose-100',
};

const DetailContent = ({
  need,
  isOwner,
  isProfessional,
  isAuthenticated,
  message,
  setMessage,
  proposedPrice,
  setProposedPrice,
  feedback,
  error,
  isResponding,
  isUpdating,
  handleRespond,
  handleStatus,
  handleClose,
}) => {
  const tx = String(need.transaction_type || '').toLowerCase();
  const st = String(need.status || 'open').toLowerCase();

  return (
    <div className="space-y-5 max-w-3xl">
      <Link
        to="/notice-board"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
      >
        <Icon name="ArrowLeft" size={16} />
        Back to notice board
      </Link>

      {/* Request card */}
      <article className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 sm:px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span
              className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${
                txStyles[tx] || 'bg-slate-50 text-slate-600 border-slate-200'
              }`}
            >
              {need.transaction_type || 'need'}
            </span>
            {need.property_type && (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                {need.property_type}
              </span>
            )}
            <span
              className={`text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full border ${
                statusStyles[st] || statusStyles.open
              }`}
            >
              {need.status || 'open'}
            </span>
            {need.urgency && (
              <span className="text-[10px] font-semibold capitalize px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
                {need.urgency}
              </span>
            )}
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            {need.title}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Posted by{' '}
            <span className="font-medium text-slate-700">
              {need.customer?.name || 'User'}
            </span>
            {need.created_at
              ? ` · ${new Date(need.created_at).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}`
              : ''}
          </p>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-5">
          <p className="text-slate-600 whitespace-pre-wrap leading-relaxed text-sm sm:text-base">
            {need.description || 'No additional description.'}
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            {[
              {
                icon: 'MapPin',
                label: 'Location',
                value:
                  [need.area, need.city, need.location]
                    .filter(Boolean)
                    .join(', ') || 'Flexible',
              },
              {
                icon: 'Wallet',
                label: 'Budget',
                value:
                  need.budget_min != null || need.budget_max != null
                    ? `${need.currency || 'UGX'} ${
                        need.budget_min?.toLocaleString() || '—'
                      } – ${need.budget_max?.toLocaleString() || '—'}`
                    : 'Flexible',
              },
              {
                icon: 'Bed',
                label: 'Beds / baths',
                value: `${need.bedrooms ?? '—'} / ${need.bathrooms ?? '—'}`,
              },
              {
                icon: 'User',
                label: 'Posted by',
                value: need.customer?.name || 'User',
              },
            ].map((row) => (
              <div
                key={row.label}
                className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3"
              >
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                  <Icon name={row.icon} size={13} />
                  {row.label}
                </div>
                <p className="text-sm font-semibold text-slate-900">{row.value}</p>
              </div>
            ))}
            {need.amenities && (
              <div className="sm:col-span-2 rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                  <Icon name="ListChecks" size={13} />
                  Requirements
                </div>
                <p className="text-sm font-medium text-slate-800">
                  {need.amenities}
                </p>
              </div>
            )}
          </div>

          {isOwner && need.status === 'open' && (
            <div className="pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={handleClose}>
                Close this request
              </Button>
            </div>
          )}
        </div>
      </article>

      {error && (
        <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm">
          {error}
        </div>
      )}
      {feedback && (
        <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm">
          {feedback}
        </div>
      )}

      {/* Agent response form */}
      {!isOwner && need.status === 'open' && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 sm:px-6 py-4 border-b border-slate-100 bg-slate-50/80">
            <h2 className="text-base font-semibold text-slate-900">
              Respond as a Real Estate Agent
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Propose a match — the seeker can accept and connect with you.
            </p>
          </div>
          <div className="p-5 sm:p-6">
            {!isAuthenticated ? (
              <p className="text-sm text-slate-600">
                <Link
                  to="/pro/login"
                  className="text-sky-700 font-semibold underline underline-offset-2"
                >
                  Sign in
                </Link>{' '}
                as an agent, broker, or advisor to propose a match.
              </p>
            ) : !isProfessional ? (
              <p className="text-sm text-slate-600">
                Only agents, brokers, and advisors can respond.{' '}
                <Link
                  to="/agent-dashboard/verification"
                  className="text-sky-700 font-semibold underline underline-offset-2"
                >
                  Complete agent verification
                </Link>
                .
              </p>
            ) : (
              <form onSubmit={handleRespond} className="space-y-3">
                <textarea
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm min-h-[110px] focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-400"
                  placeholder="How you can help, available properties, next steps…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
                <input
                  type="number"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-400"
                  placeholder="Proposed price (optional, UGX)"
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value)}
                />
                <Button
                  type="submit"
                  loading={isResponding}
                  disabled={isResponding}
                  iconName="Send"
                  iconPosition="left"
                >
                  Send response
                </Button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Responses */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">
            Responses
          </h2>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
            {need.responses?.length || 0}
          </span>
        </div>
        <div className="p-5 sm:p-6">
          {!need.responses?.length ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                <Icon name="MessageSquare" size={20} className="text-slate-400" />
              </div>
              <p className="text-sm text-slate-500">
                No agent responses yet.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {need.responses.map((r) => {
                const rs = String(r.status || 'pending').toLowerCase();
                return (
                  <li
                    key={r.id}
                    className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="font-semibold text-slate-900 flex items-center gap-2">
                          {r.responder?.name || 'Agent'}
                          {r.responder?.verification_status === 'verified' && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                              <Icon name="BadgeCheck" size={14} />
                              Verified
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500 capitalize mt-0.5">
                          {r.responder?.account_role}
                          {r.responder?.company_name
                            ? ` · ${r.responder.company_name}`
                            : ''}
                        </p>
                      </div>
                      <span
                        className={`text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full border ${
                          statusStyles[rs] || statusStyles.pending
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap mb-2 leading-relaxed">
                      {r.message}
                    </p>
                    {r.proposed_price != null && (
                      <p className="text-sm font-medium text-slate-800 mb-2">
                        Proposed:{' '}
                        <span className="text-sky-700">
                          UGX {Number(r.proposed_price).toLocaleString()}
                        </span>
                      </p>
                    )}
                    {isOwner && r.status === 'pending' && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                        <Button
                          size="sm"
                          disabled={isUpdating}
                          onClick={() => handleStatus(r.id, 'accepted')}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isUpdating}
                          onClick={() => handleStatus(r.id, 'declined')}
                        >
                          Decline
                        </Button>
                      </div>
                    )}
                    {isOwner && r.status === 'accepted' && r.responder && (
                      <div className="mt-3 text-sm p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800">
                        <p className="font-semibold mb-0.5">Contact shared</p>
                        <p>
                          {r.responder.email || '—'} ·{' '}
                          {r.responder.mobile || '—'}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const PublicShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex flex-col">
    <Header />
    <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-8">
      {children}
    </main>
    <Footer />
  </div>
);

const DetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const customerId = useSelector(selectCustomerId);
  const user = useSelector(selectCurrentUser);
  const { data: need, isLoading, refetch } = useGetPropertyNeedQuery(id);
  const [respond, { isLoading: isResponding }] =
    useRespondToPropertyNeedMutation();
  const [updateStatus, { isLoading: isUpdating }] =
    useUpdatePropertyNeedResponseStatusMutation();
  const [closeNeed] = useClosePropertyNeedMutation();

  const [message, setMessage] = useState('');
  const [proposedPrice, setProposedPrice] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  const isOwner =
    need && customerId && String(need.customer_id) === String(customerId);
  const isProfessional = isProfessionalRole(user?.account_role);

  const handleRespond = async (e) => {
    e.preventDefault();
    setError('');
    setFeedback('');
    if (!isAuthenticated) {
      navigate('/pro/login', {
        state: { from: { pathname: `/notice-board/${id}` } },
      });
      return;
    }
    if (!message.trim()) {
      setError('Please write a short proposal message.');
      return;
    }
    try {
      const res = await respond({
        id,
        message: message.trim(),
        proposed_price: proposedPrice === '' ? null : Number(proposedPrice),
      }).unwrap();
      if (res?.error) {
        setError(res.message || 'Could not send response');
        return;
      }
      trackEvent('notice_board_respond', { need_id: id }, 'lead');
      setFeedback(res.message || 'Response sent.');
      setMessage('');
      setProposedPrice('');
      refetch();
    } catch (err) {
      setError(err?.data?.message || 'Failed to respond.');
    }
  };

  const handleStatus = async (responseId, status) => {
    setError('');
    try {
      await updateStatus({ responseId, status }).unwrap();
      refetch();
    } catch (err) {
      setError(err?.data?.message || 'Could not update status');
    }
  };

  const handleClose = async () => {
    if (
      !window.confirm(
        'Close this need? Agents will no longer be able to respond.'
      )
    )
      return;
    try {
      await closeNeed(id).unwrap();
      refetch();
    } catch (err) {
      setError(err?.data?.message || 'Could not close need');
    }
  };

  const loadingUi = (
    <div className="flex items-center justify-center py-20 text-slate-500 text-sm">
      Loading request…
    </div>
  );

  const notFoundUi = (
    <div className="text-center py-16">
      <p className="text-slate-600 mb-4">
        This request was not found or is no longer public.
      </p>
      <Link
        to="/notice-board"
        className="text-sky-700 font-semibold underline underline-offset-2"
      >
        Back to notice board
      </Link>
    </div>
  );

  const body = isLoading ? (
    loadingUi
  ) : !need ? (
    notFoundUi
  ) : (
    <DetailContent
      need={need}
      isOwner={isOwner}
      isProfessional={isProfessional}
      isAuthenticated={isAuthenticated}
      message={message}
      setMessage={setMessage}
      proposedPrice={proposedPrice}
      setProposedPrice={setProposedPrice}
      feedback={feedback}
      error={error}
      isResponding={isResponding}
      isUpdating={isUpdating}
      handleRespond={handleRespond}
      handleStatus={handleStatus}
      handleClose={handleClose}
    />
  );

  if (isAuthenticated && isProfessional) {
    return (
      <AgentLayout
        title="Request details"
        subtitle="Review and respond to this property need"
      >
        {body}
      </AgentLayout>
    );
  }

  if (isAuthenticated) {
    return (
      <UserLayout
        title="Request details"
        subtitle="Track responses from verified agents"
      >
        {body}
      </UserLayout>
    );
  }

  return <PublicShell>{body}</PublicShell>;
};

export default DetailPage;
